import { useId, useRef, useState } from "react";
// Adminbereich-Links deuten oft darauf hin, dass jemand den offiziellen
// Shopify-Support sucht (Abrechnung, Kündigung, Login) statt eines Freelancers.
const ADMIN_SHOPIFY_RE = /(^|\.)admin\.shopify\.com/i;
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { trackLeadConversion, trackFormStart } from "@/lib/gtag";
import { trackUetLead } from "@/lib/uet";
import { trackOaiqLead, readOaiqAttribution } from "@/lib/oaiq";
import { submitLead } from "@/lib/leads.functions";
import { ThankYouNextSteps } from "@/components/ThankYouNextSteps";
import { normalizeShopUrl } from "@/lib/lead-validation";
import { canTrackAds } from "@/lib/consent";

const EMAIL = "info@shopify-freelancer.net";

type Lang = "de" | "en";

const dict = {
  de: {
    headline: "Kostenlose Einschätzung erhalten",
    independent:
      "Ich bin ein unabhängiger Shopify-Freelancer, nicht der offizielle Shopify-Support.",
    adminHint:
      "Das ist ein Link zu Ihrem Shopify-Adminbereich. Für Fragen zu Abrechnung, Kündigung oder Login wenden Sie sich bitte an den offiziellen Shopify-Support.",
    cta: "Einschätzung anfordern",
    sending: "Wird gesendet…",
    emailLabel: "E-Mail",
    shopLabel: "Shop-Adresse (optional)",
    shopPlaceholder: "Website",
    messageLabel: "Ihr Anliegen",
    messagePlaceholder: "Beschreiben Sie kurz Ihr Vorhaben.",
    trust: "Antwort in 48 Stunden, kostenlos und unverbindlich",
    error: `Da ist etwas schiefgelaufen. Schreiben Sie mir direkt an ${EMAIL}.`,
    sentTitle: "Vielen Dank, Ihre Anfrage ist eingegangen.",
    sentText:
      "Ich melde mich innerhalb von 48 Stunden persönlich bei Ihnen mit einer kostenlosen Ersteinschätzung.",
    privacy: "Datenschutz",
    errEmail: "Bitte geben Sie eine gültige E-Mail-Adresse an.",
    errShop: "Bitte eine gültige Website angeben.",
    errMessage: "Bitte beschreiben Sie kurz Ihr Vorhaben.",
  },
  en: {
    headline: "Get a free assessment",
    independent: "I am an independent Shopify freelancer, not the official Shopify support.",
    adminHint:
      "That looks like a link to your Shopify admin. For billing, cancellation or login questions please contact the official Shopify support.",
    cta: "Request assessment",
    sending: "Sending…",
    emailLabel: "Email",
    shopLabel: "Store address (optional)",
    shopPlaceholder: "Website",
    messageLabel: "Your enquiry",
    messagePlaceholder: "Briefly describe your project.",
    trust: "Reply in 48 hours, free and without obligation",
    error: `Something went wrong. Email me at ${EMAIL}.`,
    sentTitle: "Thank you, your request has been received.",
    sentText: "I will get back to you personally within 48 hours with a free first assessment.",
    privacy: "Privacy",
    errEmail: "Please enter a valid email address.",
    errShop: "Please enter a valid website.",
    errMessage: "Please describe your project briefly.",
  },
} as const;

type Props = {
  compact?: boolean;
  headline?: string;
  sub?: string;
  cta?: string;
  source?: string;
  lang?: Lang;
  showServices?: boolean;
  defaultService?: string;
  messagePlaceholder?: string;
  tracking?: "all" | "google";
};

export function LeadForm({
  compact = true,
  headline,
  sub,
  cta,
  source = "website",
  lang = "de",
  showServices: _showServices,
  defaultService,
  messagePlaceholder,
  tracking = "all",
}: Props) {
  const t = dict[lang];
  const uid = useId();
  const emailId = `${uid}-email`;
  const shopId = `${uid}-shop`;
  const messageId = `${uid}-message`;

  const [sent, setSent] = useState(false);
  const [shopValue, setShopValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; text: string } | null>(null);
  const inFlight = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const startedRef = useRef(false);
  const send = useServerFn(submitLead);

  /** Einmal pro Besuch: jemand hat mit dem Ausfüllen begonnen. */
  function handleFormStart() {
    if (startedRef.current) return;
    startedRef.current = true;
    try {
      trackFormStart(source);
    } catch {
      /* ignore */
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inFlight.current) return;

    const form = e.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") ?? "").trim().slice(0, 200);
    const rawShop = String(data.get("shop") ?? "").trim().slice(0, 200);
    const note = String(data.get("message") ?? "").trim().slice(0, 1000);

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setFieldError({ field: "email", text: t.errEmail });
      form.querySelector<HTMLElement>('[name="email"]')?.focus();
      return;
    }
    let shop = "";
    if (rawShop) {
      shop = normalizeShopUrl(rawShop) ?? "";
      if (!shop) {
        setFieldError({ field: "shop", text: t.errShop });
        form.querySelector<HTMLElement>('[name="shop"]')?.focus();
        return;
      }
    }
    if (!note) {
      setFieldError({ field: "message", text: t.errMessage });
      form.querySelector<HTMLElement>('[name="message"]')?.focus();
      return;
    }
    setFieldError(null);

    const message = [defaultService ? `Anliegen: ${defaultService}` : "", note]
      .filter(Boolean)
      .join("\n")
      .slice(0, 1000);

    inFlight.current = true;
    setLoading(true);
    setFormError(null);

    let eventId = "";
    try {
      const attribution = readOaiqAttribution();
      const path =
        typeof window !== "undefined" &&
        /^\/[A-Za-z0-9\-/_]{0,120}$/.test(window.location.pathname)
          ? window.location.pathname
          : undefined;
      const result = await send({
        data: { shop, email, message, source, attribution, path },
      });
      if (!result?.ok) throw new Error("not ok");
      eventId = result.eventId ?? "";
    } catch {
      setFormError(t.error);
      setLoading(false);
      inFlight.current = false;
      return;
    }

    try {
      trackLeadConversion(110);
    } catch {
      /* ignore */
    }
    // UET und OAIQ nur bei erteilter Einwilligung bzw. außerhalb der
    // Einwilligungsregionen. Google Ads wird über Consent Mode gefiltert.
    const allowed = await canTrackAds();
    if (allowed) {
      try {
        trackOaiqLead(eventId);
      } catch {
        /* ignore */
      }
      if (tracking === "all") {
        try {
          trackUetLead(110, email);
        } catch {
          /* ignore */
        }
      }
    }

    setLoading(false);
    inFlight.current = false;
    setSent(true);
  }

  const fieldCls =
    "w-full rounded-xl border border-input bg-background/60 px-4 py-3 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground/70 focus:border-primary focus:bg-background focus-visible:ring-2 focus-visible:ring-primary/40";
  const labelCls = "text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground";

  if (sent) {
    return (
      <div
        data-lead-block
        className="shadow-lift rounded-2xl border border-primary/30 bg-card p-6 md:p-8"
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/15">
          <Check className="h-6 w-6 text-primary" />
        </div>
        <h3 className="mt-4 text-lg font-semibold">{t.sentTitle}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{t.sentText}</p>
        <ThankYouNextSteps compact lang={lang} />
      </div>
    );
  }

  const errFor = (field: string) => (fieldError?.field === field ? fieldError.text : null);

  return (
    <div
      data-lead-block
      className="shadow-lift relative overflow-hidden rounded-2xl border border-border/70 bg-card p-5 md:p-6"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-16 h-48 w-48 rounded-full opacity-60 blur-3xl"
        style={{ background: "radial-gradient(circle, var(--gradient-veil), transparent 70%)" }}
      />
      <div className="relative">
        <h3 className="font-display text-lg font-semibold tracking-tight">
          {headline ?? t.headline}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
          {sub ?? (lang === "de"
            ? "Sie erhalten eine persönliche Einschätzung, den sinnvollsten nächsten Schritt und, soweit möglich, einen Kostenrahmen."
            : "You receive a personal assessment, the best next step and, where possible, a cost range.")}
        </p>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          onFocusCapture={handleFormStart}
          className="mt-4 grid gap-3.5"
          noValidate
        >
          <div className="grid gap-1.5">
            <label htmlFor={emailId} className={labelCls}>
              {t.emailLabel}
            </label>
            <input
              id={emailId}
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              maxLength={200}
              aria-invalid={errFor("email") ? true : undefined}
              aria-describedby={errFor("email") ? `${emailId}-err` : undefined}
              className={fieldCls}
              placeholder={t.emailLabel}
            />
            {errFor("email") && (
              <p id={`${emailId}-err`} role="alert" className="text-xs text-destructive">
                {errFor("email")}
              </p>
            )}
          </div>

          <div className="grid gap-1.5">
            <label htmlFor={shopId} className={labelCls}>
              {t.shopLabel}
            </label>
            <input
              id={shopId}
              name="shop"
              type="text"
              inputMode="url"
              autoComplete="url"
              maxLength={200}
              aria-invalid={errFor("shop") ? true : undefined}
              aria-describedby={
                errFor("shop")
                  ? `${shopId}-err`
                  : ADMIN_SHOPIFY_RE.test(shopValue)
                    ? `${shopId}-hint`
                    : undefined
              }
              className={fieldCls}
              placeholder={t.shopPlaceholder}
              value={shopValue}
              onChange={(e) => setShopValue(e.target.value)}
            />
            {!errFor("shop") && ADMIN_SHOPIFY_RE.test(shopValue) && (
              <p id={`${shopId}-hint`} className="text-xs text-muted-foreground">
                {t.adminHint}{" "}
                <a
                  href="https://help.shopify.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4"
                >
                  help.shopify.com
                </a>
              </p>
            )}
            {errFor("shop") && (
              <p id={`${shopId}-err`} role="alert" className="text-xs text-destructive">
                {errFor("shop")}
              </p>
            )}
          </div>

          <div className="grid gap-1.5">
            <label htmlFor={messageId} className={labelCls}>
              {t.messageLabel}
            </label>
            <textarea
              id={messageId}
              name="message"
              rows={compact ? 3 : 4}
              maxLength={1000}
              aria-invalid={errFor("message") ? true : undefined}
              aria-describedby={errFor("message") ? `${messageId}-err` : undefined}
              className={`${fieldCls} resize-none`}
              placeholder={messagePlaceholder ?? t.messagePlaceholder}
            />
            {errFor("message") && (
              <p id={`${messageId}-err`} role="alert" className="text-xs text-destructive">
                {errFor("message")}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="shadow-signal mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-signal focus-visible:ring-2 focus-visible:ring-primary/60 disabled:translate-y-0 disabled:opacity-70"
          >
            {loading ? t.sending : (cta ?? t.cta)}
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUpRight className="h-4 w-4" />
            )}
          </button>

          {formError && (
            <p role="alert" className="text-xs text-destructive">
              {formError}{" "}
              <a href={`mailto:${EMAIL}`} className="underline underline-offset-4">
                {EMAIL}
              </a>
            </p>
          )}
        </form>

        <div className="mt-4 border-t border-border/50 pt-3.5">
          <p className="text-[11px] font-medium text-foreground">{t.trust}</p>
          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground">{t.independent}</p>
            <a
              href={lang === "de" ? "/datenschutz" : "/en/privacy"}
              className="text-[11px] text-primary underline underline-offset-4 transition-opacity hover:opacity-80"
            >
              {t.privacy}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
